var _ = require("../uam/fields.module.js")(module.exports, {
    namespace: "style",
    title: "CSS Style",
    description: "Validates a CSS style string (e.g., color: red; font-size: 14px;).",
    type: "field",
    use: {
        namespace: "ua.text"
    },
    fields: {
        limitTo: {
            title: "Limit to Known Styles",
            type: "list",
            of: "string",
            description: "If provided, only these styles will be allowed. Any style not in this list will be considered invalid."
        }
    },
    tests: {
        validStyle: {
            must: true,
            run: function () {
                return validate({}, "color: red; font-size: 14px;");
            }
        },
        invalidMissingColon: {
            throw: "fields.style.invalid",
            run: function () {
                return validate({}, "color red;");
            }
        },
        invalidEmpty: {
            throw: "fields.style.invalid",
            run: function () {
                return validate({}, "");
            }
        }
    },
    errors: {
        invalid: {
            title: "Invalid Style",
            description: "Must be a valid CSS style string.",
            template: "Invalid style value: ${value}"
        },
        notAllowed: {
            title: "Style Not Allowed",
            description: "The provided style is not allowed in this context.",
            template: "Style '${style}' is not allowed. Allowed styles are: ${allowed}"
        }
    }
});

/**
 * Validates a CSS style string against the defined rules.
 * @param {*} spec The specification object, which may include a `limitTo` field.
 * @param {*} value The CSS style string to validate.
 * The string should be in the format "property: value; property2: value2;"
 * Each property-value pair should be separated by a semicolon.
 * @throws {Error} If the style string is invalid or contains disallowed styles.
 * @returns {void}
 */
function validate(spec, value) {
    if (typeof value !== "string" || value.trim() === "") {
        _.errors.invalid.throw({ value: value });
    }

    var entries = value.split(";");
    for (var i = 0; i < entries.length; i++) {
        var rule = entries[i].trim();
        if (rule === "") continue;
        if (rule.indexOf(":") === -1) {
            _.errors.invalid.throw({ value: value });
        }

        if ("limitTo" in spec) {
            var [property, _] = rule.split(":").map(s => s.trim());
            if (!spec.limitTo.includes(property)) {
                _.errors.notAllowed.throw({
                    style: property,
                    allowed: spec.limitTo.join(", ")
                });
            }
        }

    }
}
module.exports.validate = validate;


/**
 * Parses a CSS style string into an object.
 * @param {string} styleStr The CSS style string to parse.
 * The string should be in the format "property: value; property2: value2;".
 * Each property-value pair should be separated by a semicolon.
 * @returns {Object} An object where keys are CSS properties and values are their corresponding values.
 */
function parseString(styleStr) {
    const rules = {};
    let i = 0, key = '', value = '', state = 'key';
    let quote = null, parens = 0;

    const flushRule = () => {
        if (key.trim() && value.trim()) {
            rules[key.trim()] = value.trim();
        }
        key = '';
        value = '';
        state = 'key';
    };

    while (i < styleStr.length) {
        const char = styleStr[i];

        if (state === 'key') {
            if (char === ':') {
                state = 'value';
            } else {
                key += char;
            }
        } else if (state === 'value') {
            if (quote) {
                value += char;
                if (char === quote) quote = null;
            } else if (char === '"' || char === "'") {
                value += char;
                quote = char;
            } else if (char === '(') {
                value += char;
                parens++;
            } else if (char === ')') {
                value += char;
                if (parens > 0) parens--;
            } else if (char === ';' && parens === 0) {
                flushRule();
            } else {
                value += char;
            }
        }

        i++;
    }

    if (key && value) {
        flushRule();
    }

    return rules;
} module.exports.parseString = parseString;

/**
 * Merges multiple CSS style strings or objects into a single string.
 * @param  {...any} styles The css styles to merge. Can be strings or objects.
 * @returns {string} A single CSS style string.
 */
function merge(...styles) {
    const merged = {};

    styles.forEach(style => {
        if (typeof style === "string") {
            const parsed = parseString(style);
            for (const [prop, val] of Object.entries(parsed)) {
                merged[prop] = val; // Overwrites duplicates
            }
        } else if (typeof style === "object" && style !== null) {
            for (const [prop, val] of Object.entries(style)) {
                merged[prop] = val;
            }
        }
    });

    return Object.entries(merged)
        .map(([k, v]) => `${k}: ${v}`)
        .join("; ");
} module.exports.merge = merge;
